import React, { useRef, useState } from 'react'
import PropTypes from 'prop-types'
import { Button, OverlayTrigger, Popover } from 'react-bootstrap'
import Select from 'react-select'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFilter, faTimes } from '@fortawesome/free-solid-svg-icons'
import FilterEditor from 'shared/column-filters/FilterEditor'
import { groupBy } from 'lodash'
import { smSelectStyles } from 'charts/searchable-column-select'
import { summarizeLeaf } from './summary'

// One filter in a group: a column, and its value set through that column's header editor.
// `ui` is the editor's state; `filter` the API params rebuilt from it on every change.
const FilterLeaf = ({ leaf, filterColumns, onChange, onRemove, canEdit, dashboardId }) => {
  const [editing, setEditing] = useState(false)
  const applyRef = useRef(null)
  const filterColumn = filterColumns.find(({ column }) => column.accessor_key === leaf.column)

  const setUi = (next) =>
    onChange((current) => {
      const ui = typeof next === 'function' ? next(current.ui) : next
      return { ...current, ui, filter: filterColumn.filterFor(ui) }
    })

  const chooseColumn = (accessorKey) =>
    onChange((current) => ({ ...current, column: accessorKey || null, ui: {}, filter: {} }))

  const closeEditor = () => {
    applyRef.current?.()
    setEditing(false)
  }

  const editor = filterColumn && (
    <Popover className="filterable-header-popover">
      <Popover.Body>
        <FilterEditor
          column={filterColumn.column}
          params={leaf.ui || {}}
          setParams={setUi}
          options={filterColumn.options}
          dashboardId={dashboardId}
          canEdit={canEdit}
          onClose={() => setEditing(false)}
          onSetHandleApply={(handleApply) => {
            applyRef.current = handleApply
          }}
        />
      </Popover.Body>
    </Popover>
  )

  const summary = summarizeLeaf(leaf, filterColumn?.column)

  // Searchable and grouped under the headings buildFilterColumns gives, since a module can offer
  // well over a hundred columns.
  const columnOptions = Object.entries(groupBy(filterColumns, ({ group }) => group || 'Other')).map(
    ([group, entries]) => ({
      label: group,
      options: entries.map(({ column }) => ({ value: column.accessor_key, label: column.display_name || column.name }))
    })
  )

  return (
    <div className="d-flex align-items-center gap-2 mb-2" data-testid="filter-leaf">
      <div style={{ width: 260 }} data-testid="filter-leaf-column" data-value={leaf.column || ''}>
        <Select
          inputId={`filter-leaf-column-${leaf.id}`}
          classNamePrefix="filter-leaf-column"
          options={columnOptions}
          value={columnOptions.flatMap(({ options }) => options).find((option) => option.value === leaf.column) || null}
          onChange={(option) => chooseColumn(option?.value)}
          placeholder="Choose a column…"
          isDisabled={!canEdit}
          styles={smSelectStyles}
          menuPortalTarget={document.body}
        />
      </div>

      {filterColumn && (
        // Flips above the row when there is no room below, as in a long chart settings modal.
        <OverlayTrigger
          trigger="click"
          placement="bottom"
          flip
          overlay={editor}
          show={editing}
          rootClose
          onToggle={(show) => (show ? setEditing(true) : closeEditor())}
        >
          <Button
            variant={summary ? 'primary' : 'light'}
            className={summary ? '' : 'text-primary'}
            size="sm"
            data-testid="filter-leaf-edit"
          >
            <FontAwesomeIcon icon={faFilter} size="xs" className="me-1" />
            {summary || 'Set value'}
          </Button>
        </OverlayTrigger>
      )}

      {canEdit && (
        <Button
          variant="light"
          className="text-primary ms-auto"
          size="sm"
          title="Remove filter"
          onClick={onRemove}
          data-testid="filter-leaf-remove"
        >
          <FontAwesomeIcon icon={faTimes} />
        </Button>
      )}
    </div>
  )
}

FilterLeaf.propTypes = {
  leaf: PropTypes.object.isRequired,
  filterColumns: PropTypes.array.isRequired,
  onChange: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
  canEdit: PropTypes.bool,
  dashboardId: PropTypes.string
}

export default FilterLeaf
